<template>
  <div class="asset-notification-rules">
    <div class="card">
      <div class="card-header border-0 pt-6">
        <div class="card-title">
          <h3>{{ $t('components.assetNotificationRules.title') }}</h3>
        </div>
        <div class="card-toolbar">
          <button
            v-if="!isReadOnly"
            type="button"
            class="btn btn-sm btn-primary"
            @click="openCreateModal"
          >
            <i class="bi bi-plus-circle" /> {{ $t('components.assetNotificationRules.create') }}
          </button>
        </div>
      </div>

      <div class="card-body pt-0">
        <DxDataGrid
          ref="rulesGrid"
          :data-source="rulesDataSource"
          :allow-column-reordering="true"
          :allow-column-resizing="true"
          :column-auto-width="true"
          :hover-state-enabled="true"
          :show-borders="true"
          :show-column-lines="true"
          :remote-operations="true"
          key-expr="id"
        >
          <DxPaging :page-size="20" />
          <DxPager
            :allowed-page-sizes="[10, 20, 50]"
            :show-info="true"
            :show-navigation-buttons="true"
            :show-page-size-selector="true"
            :visible="true"
          />
          <DxSearchPanel
            :visible="true"
            :placeholder="$t('components.assetNotificationRules.search')"
          />

          <DxColumn
            data-field="name"
            :caption="$t('components.assetNotificationRules.columns.name')"
            :width="180"
          />

          <DxColumn
            data-field="targetAsset.name"
            :caption="$t('components.assetNotificationRules.columns.target')"
            :width="180"
            cell-template="targetAssetTemplate"
          />

          <DxColumn
            data-field="canParamsIo.paramName"
            :caption="$t('components.assetNotificationRules.columns.parameter')"
            :width="150"
            cell-template="parameterTemplate"
          />

          <DxColumn
            data-field="conditionType"
            :caption="$t('components.assetNotificationRules.columns.condition')"
            :width="120"
            cell-template="conditionTemplate"
          />

          <DxColumn
            data-field="notificationChannels"
            :caption="$t('components.assetNotificationRules.columns.channels')"
            :width="200"
            cell-template="channelsTemplate"
          />

          <DxColumn
            data-field="isActive"
            :caption="$t('components.assetNotificationRules.columns.active')"
            data-type="boolean"
            :width="80"
            alignment="center"
          />

          <DxColumn
            v-if="!isReadOnly"
            :caption="$t('components.assetNotificationRules.columns.actions')"
            :width="170"
            alignment="center"
            cell-template="actionsTemplate"
          />

          <template #targetAssetTemplate="{ data }">
            <div class="d-flex flex-column">
              <span>{{ getTargetDisplayName(data.data) }}</span>
              <small class="text-muted">{{ getTargetDisplayMeta(data.data) }}</small>
            </div>
          </template>

          <template #actionsTemplate="{ data }">
            <div class="d-flex align-items-center justify-content-center gap-2">
              <button
                type="button"
                class="btn btn-icon btn-sm btn-light-primary"
                :title="$t('components.assetNotificationRules.edit')"
                @click="openEditModal({ row: data })"
              >
                <i class="bi bi-pencil" />
              </button>
              <button
                type="button"
                class="btn btn-icon btn-sm"
                :class="data.data.isActive ? 'btn-light-warning' : 'btn-light-success'"
                :title="data.data.isActive
                  ? $t('components.assetNotificationRules.deactivate')
                  : $t('components.assetNotificationRules.activate')"
                @click="toggleRuleActiveState(data.data)"
              >
                <i :class="data.data.isActive ? 'bi bi-pause-circle' : 'bi bi-play-circle'" />
              </button>
              <button
                type="button"
                class="btn btn-icon btn-sm btn-light-danger"
                :title="$t('components.assetNotificationRules.deletePermanent')"
                @click="deleteRule(data.data)"
              >
                <i class="bi bi-trash" />
              </button>
            </div>
          </template>

          <template #parameterTemplate="{ data }">
            <span>{{ getParameterDisplayName(data.data.canParamsIo?.paramName) }}</span>
          </template>

          <template #conditionTemplate="{ data }">
            <span>{{ getConditionDisplay(data.data) }}</span>
          </template>

          <template #channelsTemplate="{ data }">
            <div>
              <span
                v-for="channel in data.data.notificationChannels"
                :key="channel"
                class="badge me-1"
                :class="{
                  'badge-primary': channel === 'email',
                  'badge-success': channel === 'sms',
                  'badge-info': channel === 'mercure'
                }"
              >
                {{ channel.toUpperCase() }}
              </span>
            </div>
          </template>
        </DxDataGrid>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      class="modal fade"
      id="ruleModal"
      tabindex="-1"
      ref="ruleModalRef"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingRule ? $t('components.assetNotificationRules.editTitle') : $t('components.assetNotificationRules.createTitle') }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            />
          </div>

          <div class="modal-body">
            <form @submit.prevent="saveRule">
              <div class="mb-3">
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.name') }}</label>
                <input
                  v-model="formData.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validationErrors.name }"
                  required
                >
                <div
                  v-if="validationErrors.name"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.assetNotificationRules.validation.nameRequired') }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">{{ $t('components.assetNotificationRules.form.description') }}</label>
                <textarea
                  v-model="formData.description"
                  class="form-control"
                  rows="2"
                />
              </div>

              <div class="mb-3">
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.targetType') }}</label>
                <select
                  v-model="formData.targetType"
                  class="form-select"
                  @change="onTargetTypeChange"
                >
                  <option value="asset">
                    {{ $t('components.assetNotificationRules.form.targetAsset') }}
                  </option>
                  <option value="group">
                    {{ $t('components.assetNotificationRules.form.targetGroup') }}
                  </option>
                  <option
                    v-if="hasWirepas"
                    value="gateway"
                  >
                    {{ $t('components.assetNotificationRules.form.targetGateway') }}
                  </option>
                </select>
              </div>

              <div
                v-if="formData.targetType === 'asset'"
                class="mb-3"
              >
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.targetAsset') }}</label>
                <select
                  v-model="formData.targetAssetId"
                  class="form-select"
                  :class="{ 'is-invalid': validationErrors.targetAssetId }"
                  required
                  @change="onAssetChange"
                >
                  <option :value="null">
                    {{ $t('components.assetNotificationRules.form.selectAsset') }}
                  </option>
                  <option
                    v-for="asset in availableAssets"
                    :key="asset.id"
                    :value="asset.id"
                  >
                    {{ asset.displayName || asset.name || asset.serialNumber }}
                  </option>
                </select>
                <div
                  v-if="validationErrors.targetAssetId"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.assetNotificationRules.validation.targetAssetRequired') }}
                </div>
              </div>

              <div
                v-else-if="formData.targetType === 'group'"
                class="mb-3"
              >
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.targetGroup') }}</label>
                <select
                  v-model="formData.targetAssetGroupId"
                  class="form-select"
                  :class="{ 'is-invalid': validationErrors.targetAssetGroupId }"
                  required
                  @change="onAssetGroupChange"
                >
                  <option :value="null">
                    {{ $t('components.assetNotificationRules.form.selectGroup') }}
                  </option>
                  <option
                    v-for="group in availableGroups"
                    :key="group.id"
                    :value="group.id"
                  >
                    {{ group.displayName }}
                  </option>
                </select>
                <div
                  v-if="validationErrors.targetAssetGroupId"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.assetNotificationRules.validation.targetAssetGroupRequired') }}
                </div>
              </div>

              <div
                v-else-if="formData.targetType === 'gateway'"
                class="mb-3"
              >
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.targetGateway') }}</label>
                <select
                  v-model="formData.targetGwId"
                  class="form-select"
                  :class="{ 'is-invalid': validationErrors.targetGwId }"
                  required
                  @change="onGatewayChange"
                >
                  <option :value="null">
                    {{ $t('components.assetNotificationRules.form.selectGateway') }}
                  </option>
                  <option
                    v-for="gateway in availableGateways"
                    :key="gateway.gwId"
                    :value="gateway.gwId"
                  >
                    {{ gateway.gwId }}
                  </option>
                </select>
                <div
                  v-if="validationErrors.targetGwId"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.assetNotificationRules.validation.targetGwIdRequired') }}
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.parameter') }}</label>
                <select
                  v-model="formData.canParamsIoId"
                  class="form-select"
                  :class="{ 'is-invalid': validationErrors.canParamsIoId }"
                  required
                  :disabled="!isTargetSelected"
                >
                  <option :value="null">
                    {{
                      isTargetSelected
                        ? $t('components.assetNotificationRules.form.selectParameter')
                        : (
                          formData.targetType === 'asset'
                            ? $t('components.assetNotificationRules.form.selectAssetFirst')
                            : formData.targetType === 'group'
                              ? $t('components.assetNotificationRules.form.selectGroupFirst')
                              : $t('components.assetNotificationRules.form.selectGatewayFirst')
                        )
                    }}
                  </option>
                  <option
                    v-for="param in availableParameters"
                    :key="param.id"
                    :value="param.id"
                  >
                    {{ getParameterDisplayName(param.paramName) }}
                  </option>
                </select>
                <div
                  v-if="validationErrors.canParamsIoId"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.assetNotificationRules.validation.parameterRequired') }}
                </div>
              </div>

              <div
                v-if="isParameterSelected && !isBooleanStateParameter"
                class="row"
              >
                <div class="col-md-6 mb-3">
                  <label class="form-label required">{{ $t('components.assetNotificationRules.form.conditionType') }}</label>
                  <select
                    v-model="formData.conditionType"
                    class="form-select"
                    required
                  >
                    <option
                      v-if="!isBinaryParameter"
                      value="greater_than"
                    >
                      {{ $t('components.assetNotificationRules.conditions.greaterThan') }}
                    </option>
                    <option
                      v-if="!isBinaryParameter"
                      value="less_than"
                    >
                      {{ $t('components.assetNotificationRules.conditions.lessThan') }}
                    </option>
                    <option value="equals">
                      {{ $t('components.assetNotificationRules.conditions.equals') }}
                    </option>
                    <option
                      v-if="!isBinaryParameter"
                      value="between"
                    >
                      {{ $t('components.assetNotificationRules.conditions.between') }}
                    </option>
                    <option
                      v-if="!isBinaryParameter"
                      value="outside_range"
                    >
                      {{ $t('components.assetNotificationRules.conditions.outsideRange') }}
                    </option>
                  </select>
                </div>

                <div class="col-md-3 mb-3">
                  <label class="form-label required">{{ $t('components.assetNotificationRules.form.thresholdValue') }}</label>
                  <input
                    v-model.number="formData.thresholdValue"
                    type="number"
                    step="0.01"
                    class="form-control"
                    required
                  >
                </div>

                <div
                  v-if="formData.conditionType === 'between' || formData.conditionType === 'outside_range'"
                  class="col-md-3 mb-3"
                >
                  <label class="form-label required">{{ $t('components.assetNotificationRules.form.thresholdValueMax') }}</label>
                  <input
                    v-model.number="formData.thresholdValueMax"
                    type="number"
                    step="0.01"
                    class="form-control"
                    required
                  >
                </div>
              </div>
              <div
                v-else-if="isParameterSelected && isBooleanStateParameter"
                class="mb-3"
              >
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.booleanState') }}</label>
                <div class="d-flex flex-wrap gap-3">
                  <div class="form-check">
                    <input
                      :checked="isBooleanThresholdTrue"
                      type="checkbox"
                      class="form-check-input"
                      id="booleanThresholdTrue"
                      @change="setBooleanThreshold(true)"
                    >
                    <label
                      class="form-check-label"
                      for="booleanThresholdTrue"
                    >
                      {{ $t('components.assetNotificationRules.form.enabled') }}
                    </label>
                  </div>
                  <div class="form-check">
                    <input
                      :checked="!isBooleanThresholdTrue"
                      type="checkbox"
                      class="form-check-input"
                      id="booleanThresholdFalse"
                      @change="setBooleanThreshold(false)"
                    >
                    <label
                      class="form-check-label"
                      for="booleanThresholdFalse"
                    >
                      {{ $t('components.assetNotificationRules.form.disabled') }}
                    </label>
                  </div>
                </div>
              </div>

              <div
                v-if="isDwellParameter"
                class="mb-3"
              >
                <label class="form-label required">{{ $t('components.assetNotificationRules.form.dwellThreshold') }}</label>
                <input
                  v-model.number="formData.timeWindowMinutes"
                  type="number"
                  min="1"
                  class="form-control"
                  :class="{ 'is-invalid': validationErrors.timeWindowMinutes }"
                  required
                >
                <div
                  v-if="validationErrors.timeWindowMinutes"
                  class="invalid-feedback d-block"
                >
                  {{ $t('components.assetNotificationRules.validation.dwellThresholdRequired') }}
                </div>
                <div
                  v-else
                  class="form-text"
                >
                  {{ $t('components.assetNotificationRules.form.dwellThresholdHint') }}
                </div>
              </div>

              <div
                v-if="isAvgParameter"
                class="mb-3"
              >
                <label class="form-label">{{ $t('components.assetNotificationRules.form.timeWindow') }}</label>
                <select
                  v-model="formData.timeWindowMinutes"
                  class="form-select"
                >
                  <option :value="null">
                    {{ $t('components.assetNotificationRules.form.defaultTimeWindow') }} (15 min)
                  </option>
                  <option
                    v-for="opt in TIME_WINDOW_OPTIONS"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>

              <div class="mb-3">
                <label class="form-label d-block">{{ $t('components.assetNotificationRules.form.notificationChannels') }}</label>
                <div class="d-flex flex-wrap gap-3">
                  <div class="form-check">
                    <input
                      v-model="formData.notificationChannels"
                      value="email"
                      type="checkbox"
                      class="form-check-input"
                      id="channelEmail"
                    >
                    <label
                      class="form-check-label"
                      for="channelEmail"
                    >
                      Email
                    </label>
                  </div>
                  <div class="form-check">
                    <input
                      v-model="formData.notificationChannels"
                      value="sms"
                      type="checkbox"
                      class="form-check-input"
                      id="channelSms"
                    >
                    <label
                      class="form-check-label"
                      for="channelSms"
                    >
                      SMS
                    </label>
                  </div>
                  <div class="form-check">
                    <input
                      v-model="formData.notificationChannels"
                      value="mercure"
                      type="checkbox"
                      class="form-check-input"
                      id="channelMercure"
                    >
                    <label
                      class="form-check-label"
                      for="channelMercure"
                    >
                      Mercure (Push)
                    </label>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <div class="form-check form-switch">
                  <input
                    v-model="formData.isActive"
                    type="checkbox"
                    class="form-check-input"
                    id="isActive"
                  >
                  <label
                    class="form-check-label"
                    for="isActive"
                  >
                    {{ $t('components.assetNotificationRules.form.isActive') }}
                  </label>
                </div>
              </div>

              <hr class="my-4">

              <div class="border rounded p-3 bg-light-subtle">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label
                      class="form-label"
                      for="repeatsPerSeries"
                    >
                      {{ $t('components.assetNotificationRules.form.repeatsPerSeries') }}
                    </label>
                    <input
                      id="repeatsPerSeries"
                      v-model.number="formData.repeatsPerSeries"
                      type="number"
                      min="0"
                      max="100"
                      class="form-control"
                      :class="{ 'is-invalid': validationErrors.repeatsPerSeries }"
                    >
                    <div
                      v-if="validationErrors.repeatsPerSeries"
                      class="invalid-feedback d-block"
                    >
                      {{ $t('components.assetNotificationRules.validation.repeatsPerSeriesRequired') }}
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label
                      class="form-label"
                      for="repeatIntervalMinutes"
                    >
                      {{ $t('components.assetNotificationRules.form.repeatIntervalMinutes') }}
                    </label>
                    <input
                      id="repeatIntervalMinutes"
                      v-model.number="formData.repeatIntervalMinutes"
                      type="number"
                      min="1"
                      class="form-control"
                      :disabled="formData.repeatsPerSeries === 0"
                      :class="{ 'is-invalid': validationErrors.repeatIntervalMinutes }"
                    >
                    <div
                      v-if="validationErrors.repeatIntervalMinutes"
                      class="invalid-feedback d-block"
                    >
                      {{ $t('components.assetNotificationRules.validation.repeatIntervalMinutesRequired') }}
                    </div>
                    <div
                      v-else-if="formData.repeatsPerSeries === 0"
                      class="form-text"
                    >
                      {{ $t('components.assetNotificationRules.form.repeatIntervalDisabledHint') }}
                    </div>
                  </div>

                  <div class="col-md-4">
                    <label
                      class="form-label"
                      :class="{ required: formData.repeatsPerSeries > 0 }"
                      for="maxMessagesPerHour"
                    >
                      {{ $t('components.assetNotificationRules.form.maxMessagesPerHour') }}
                    </label>
                    <input
                      id="maxMessagesPerHour"
                      v-model.number="formData.maxMessagesPerHour"
                      type="number"
                      min="1"
                      class="form-control"
                      :disabled="formData.repeatsPerSeries === 0"
                      :class="{ 'is-invalid': validationErrors.maxMessagesPerHour }"
                    >
                    <div
                      v-if="validationErrors.maxMessagesPerHour"
                      class="invalid-feedback d-block"
                    >
                      {{ $t('components.assetNotificationRules.validation.maxMessagesPerHourRequired') }}
                    </div>
                    <div
                      v-else-if="formData.repeatsPerSeries === 0"
                      class="form-text"
                    >
                      {{ $t('components.assetNotificationRules.form.maxMessagesDisabledHint') }}
                    </div>
                  </div>
                </div>

                <div class="mt-2 text-muted small">
                  <i class="bi bi-info-circle me-1" />
                  {{ $t('components.assetNotificationRules.form.messagesHint') }}:
                  <strong>{{ 1 + (formData.repeatsPerSeries ?? 0) }}</strong>
                  / {{ formData.repeatsPerSeries > 0 ? (formData.maxMessagesPerHour || 1) : 1 }}
                </div>
              </div>
            </form>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              @click="closeModal"
            >
              {{ $t('components.assetNotificationRules.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveRule"
              :disabled="saving"
            >
              <span
                v-if="saving"
                class="spinner-border spinner-border-sm me-2"
              />
              {{ $t('components.assetNotificationRules.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, getCurrentInstance, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useQuasar } from 'quasar'
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxSearchPanel
} from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'
import { Modal } from 'bootstrap'
import { getAssetNotificationRulesGrid, getAssetParameters, getAssetGroupParameters, getAssetGatewayParameters, getAssetNotificationRuleGateways } from '@/crud/asset-rules/getters'
import { createAssetNotificationRule, updateAssetNotificationRule, deleteAssetNotificationRule } from '@/crud/asset-rules/senders'
import { getAssetsGrid } from '@/crud/asset/getters'
import { getAssetGroupsTree } from '@/crud/asset-group/getters'
import { hasWirepas, fetchWirepasStatus } from '@/stores/wirepasStore'
import { isRestrictedAssetUser } from '@/utils/tools'
import { SERIES_META } from '@/utils/asset-history-series'

export default defineComponent({
  name: 'AssetNotificationRules',
  components: {
    DxDataGrid,
    DxColumn,
    DxPaging,
    DxPager,
    DxSearchPanel
  },

  setup() {
    const { t, te } = useI18n()
    const vm = getCurrentInstance()
    const toast = vm?.proxy?.$toast
    const quasar = useQuasar()
    const availableParameters = ref<any[]>([])
    const availableAssets = ref<any[]>([])
    const availableGroups = ref<any[]>([])
    const availableGateways = ref<any[]>([])
    const editingRule = ref<any>(null)
    const ruleModalRef = ref<HTMLElement | null>(null)
    const rulesGrid = ref<any>(null)
    const saving = ref(false)
    const showValidationErrors = ref(false)

    // Permission check
    const isReadOnly = computed(() => isRestrictedAssetUser())

    let modalInstance: Modal | null = null

    const formData = ref({
      name: '',
      description: '',
      canParamsIoId: null,
      conditionType: 'greater_than',
      thresholdValue: 0,
      thresholdValueMax: null,
      targetType: 'asset',
      targetAssetId: null,
      targetAssetGroupId: null,
      targetGwId: null as string | null,
      notificationChannels: [],
      isActive: true,
      timeWindowMinutes: null as number | null,
      repeatsPerSeries: 0,
      repeatIntervalMinutes: null as number | null,
      maxMessagesPerHour: 1
    })

    const INACTIVITY_PARAM_24H = 'ASSET_INACTIVITY_24H'
    const INACTIVITY_PARAM_30M = 'ASSET_INACTIVITY_30M'
    const INACTIVITY_PARAMS = [INACTIVITY_PARAM_24H, INACTIVITY_PARAM_30M]
    const DWELL_PARAM = 'ASSET_DWELL'
    const BOOLEAN_STATE_PARAMS = [...INACTIVITY_PARAMS, DWELL_PARAM]
    const hasPositiveValue = (value: number | null) => value !== null && value >= 1

    const mapRuleFromApi = (rule: any) => ({
      ...rule,
      canParamsIo: rule.canParamsIo || (rule.can_params_io
        ? { id: rule.can_params_io.id, paramName: rule.can_params_io.param_name }
        : null),
      conditionType: rule.conditionType || rule.condition_type,
      thresholdValue: rule.thresholdValue ?? rule.threshold_value,
      thresholdValueMax: rule.thresholdValueMax ?? rule.threshold_value_max,
      targetType: (rule.targetAssetGroup || rule.target_asset_group)
        ? 'group'
        : (rule.targetGwId || rule.target_gw_id) ? 'gateway' : 'asset',
      targetGwId: rule.targetGwId ?? rule.target_gw_id ?? null,
      targetAsset: rule.targetAsset || (rule.target_asset
        ? {
            id: rule.target_asset.id,
            name: rule.target_asset.name,
            serialNumber: rule.target_asset.serial_number
          }
        : null),
      targetAssetGroup: rule.targetAssetGroup || (rule.target_asset_group
        ? {
            id: rule.target_asset_group.id,
            name: rule.target_asset_group.name
          }
        : null),
      notificationChannels: rule.notificationChannels || rule.notification_channels || [],
      isActive: rule.isActive ?? rule.is_active,
      timeWindowMinutes: rule.timeWindowMinutes ?? rule.time_window_minutes ?? null,
      repeatsPerSeries: rule.repeatsPerSeries ?? rule.repeats_per_series ?? 0,
      repeatIntervalMinutes: rule.repeatIntervalMinutes ?? rule.repeat_interval_minutes ?? null,
      maxMessagesPerHour: rule.maxMessagesPerHour ?? rule.max_messages_per_hour ?? 1,
      createdAt: rule.createdAt || rule.created_at,
      updatedAt: rule.updatedAt || rule.updated_at
    })

    const rulesDataSource = new CustomStore({
      key: 'id',
      load: async (loadOptions: any) => {
        const skip = loadOptions.skip ?? 0
        const take = loadOptions.take ?? 20
        const searchValue = loadOptions.searchValue ?? ''

        try {
          const response = await getAssetNotificationRulesGrid(skip, take, searchValue)
          const data = response.data?.data || response.data || []
          const totalCount = response.data?.totalCount ?? data.length
          return {
            data: data.map(mapRuleFromApi),
            totalCount
          }
        } catch (error) {
          console.error('Error loading rules:', error)
          throw error
        }
      }
    })

    const mapAssetFromApi = (asset: any) => ({
      ...asset,
      serialNumber: asset.serialNumber ?? asset.serial_number,
      name: asset.name ?? asset.serial_number,
      displayName: asset.name
        ? `${asset.name} (${asset.serial_number ?? asset.serialNumber ?? ''})`
        : asset.serial_number ?? asset.serialNumber ?? ''
    })

    const loadAssets = async () => {
      try {
        const response = await getAssetsGrid(0, 500, '')
        const data = response.data?.data || response.data || []
        availableAssets.value = data.map(mapAssetFromApi)
      } catch (error) {
        console.error('Error loading assets:', error)
        availableAssets.value = []
      }
    }

    const flattenGroups = (nodes: any[], depth: number = 0): any[] => {
      return nodes.flatMap((node) => {
        const item = {
          id: node.id,
          name: node.name,
          displayName: `${'-- '.repeat(depth)}${node.name}`
        }

        return [item, ...flattenGroups(node.children || [], depth + 1)]
      })
    }

    const loadGroups = async () => {
      try {
        const response = await getAssetGroupsTree()
        const tree = response.data?.tree || response.data?.data?.tree || []
        availableGroups.value = flattenGroups(Array.isArray(tree) ? tree : [])
      } catch (error) {
        console.error('Error loading asset groups:', error)
        availableGroups.value = []
      }
    }

    const loadGateways = async () => {
      if (!hasWirepas.value) {
        availableGateways.value = []
        return
      }

      try {
        const response = await getAssetNotificationRuleGateways()
        availableGateways.value = response.data?.data || response.data || []
      } catch (error) {
        console.error('Error loading Wirepas gateways:', error)
        availableGateways.value = []
      }
    }

    const loadParametersForAsset = async (assetId: number) => {
      if (!assetId) {
        availableParameters.value = []
        return
      }

      try {
        const response = await getAssetParameters(assetId)
        availableParameters.value = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error loading parameters:', error)
        availableParameters.value = []
      }
    }

    const loadParametersForGroup = async (groupId: number) => {
      if (!groupId) {
        availableParameters.value = []
        return
      }

      try {
        const response = await getAssetGroupParameters(groupId)
        availableParameters.value = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error loading group parameters:', error)
        availableParameters.value = []
      }
    }

    const onAssetChange = () => {
      // Reset parameter selection when asset changes
      formData.value.canParamsIoId = null
      availableParameters.value = []

      // Load parameters for selected asset
      if (formData.value.targetAssetId) {
        loadParametersForAsset(formData.value.targetAssetId)
      }
    }

    const onAssetGroupChange = () => {
      formData.value.canParamsIoId = null
      availableParameters.value = []

      if (formData.value.targetAssetGroupId) {
        loadParametersForGroup(formData.value.targetAssetGroupId)
      }
    }

    const loadParametersForGateway = async (gwId: string) => {
      if (!gwId) {
        availableParameters.value = []
        return
      }

      try {
        const response = await getAssetGatewayParameters(gwId)
        availableParameters.value = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error loading gateway parameters:', error)
        availableParameters.value = []
      }
    }

    const onGatewayChange = () => {
      formData.value.canParamsIoId = null
      availableParameters.value = []

      if (formData.value.targetGwId) {
        loadParametersForGateway(formData.value.targetGwId)
      }
    }

    const onTargetTypeChange = () => {
      formData.value.canParamsIoId = null
      formData.value.targetAssetId = null
      formData.value.targetAssetGroupId = null
      formData.value.targetGwId = null
      availableParameters.value = []
    }

    const openCreateModal = () => {
      editingRule.value = null
      showValidationErrors.value = false
      formData.value = {
        name: '',
        description: '',
        canParamsIoId: null,
        conditionType: 'greater_than',
        thresholdValue: 0,
        thresholdValueMax: null,
        targetType: 'asset',
        targetAssetId: null,
        targetAssetGroupId: null,
        targetGwId: null,
        notificationChannels: [],
        isActive: true,
        timeWindowMinutes: null,
        repeatsPerSeries: 0,
        repeatIntervalMinutes: null,
        maxMessagesPerHour: 1
      }
      // Reset parameters list
      availableParameters.value = []

      if (!modalInstance && ruleModalRef.value) {
        modalInstance = new Modal(ruleModalRef.value as HTMLElement)
      }
      modalInstance?.show()
    }

    const openEditModal = async (e) => {
      const rule = e.row.data
      editingRule.value = rule
      showValidationErrors.value = false
      formData.value = {
        name: rule.name,
        description: rule.description || '',
        canParamsIoId: rule.canParamsIo?.id,
        conditionType: rule.conditionType,
        thresholdValue: rule.thresholdValue,
        thresholdValueMax: rule.thresholdValueMax,
        targetType: rule.targetType,
        targetAssetId: rule.targetAsset?.id || null,
        targetAssetGroupId: rule.targetAssetGroup?.id || null,
        targetGwId: rule.targetGwId || null,
        notificationChannels: rule.notificationChannels || [],
        isActive: rule.isActive,
        timeWindowMinutes: rule.timeWindowMinutes ?? null,
        repeatsPerSeries: rule.repeatsPerSeries ?? 0,
        repeatIntervalMinutes: rule.repeatIntervalMinutes ?? null,
        maxMessagesPerHour: rule.maxMessagesPerHour ?? 1
      }

      // Load parameters for the asset in edit mode
      if (rule.targetType === 'group' && rule.targetAssetGroup?.id) {
        await loadParametersForGroup(rule.targetAssetGroup.id)
      } else if (rule.targetType === 'gateway' && rule.targetGwId) {
        await loadParametersForGateway(rule.targetGwId)
      } else if (rule.targetAsset?.id) {
        await loadParametersForAsset(rule.targetAsset.id)
      }

      if (!modalInstance && ruleModalRef.value) {
        modalInstance = new Modal(ruleModalRef.value as HTMLElement)
      }
      modalInstance?.show()
    }

    const closeModal = () => {
      modalInstance?.hide()
    }

    const getTargetDisplayName = (rule: any) => {
      if (rule.targetAssetGroup?.name) {
        return rule.targetAssetGroup.name
      }

      if (rule.targetGwId) {
        return rule.targetGwId
      }

      return rule.targetAsset?.name || '-'
    }

    const getTargetDisplayMeta = (rule: any) => {
      if (rule.targetAssetGroup?.name) {
        return t('components.assetNotificationRules.form.targetGroup')
      }

      if (rule.targetGwId) {
        return t('components.assetNotificationRules.form.targetGateway')
      }

      return rule.targetAsset?.serialNumber
        ? `${t('components.assetNotificationRules.form.targetAsset')} • ${rule.targetAsset.serialNumber}`
        : t('components.assetNotificationRules.form.targetAsset')
    }

    const saveRule = async () => {
      try {
        showValidationErrors.value = true

        if (Object.values(validationErrors.value).some(Boolean)) {
          return
        }

        saving.value = true

        const dataToSave = { ...formData.value }
        if (dataToSave.canParamsIoId !== null) {
          dataToSave.canParamsIoId = Number(dataToSave.canParamsIoId) as any
        }

        if (dataToSave.targetType === 'group') {
          dataToSave.targetAssetId = null
          dataToSave.targetGwId = null
        } else if (dataToSave.targetType === 'gateway') {
          dataToSave.targetAssetId = null
          dataToSave.targetAssetGroupId = null
        } else {
          dataToSave.targetAssetGroupId = null
          dataToSave.targetGwId = null
        }

        if (dataToSave.repeatsPerSeries === 0) {
          dataToSave.repeatIntervalMinutes = null
          dataToSave.maxMessagesPerHour = 1
        }

        // Boolean-state params always save as equals-1/0; only dwell carries a time window.
        if (isBooleanStateParameter.value) {
          dataToSave.conditionType = 'equals'
          dataToSave.thresholdValue = dataToSave.thresholdValue ? 1 : 0
          dataToSave.thresholdValueMax = null
          dataToSave.timeWindowMinutes = isDwellParameter.value
            ? (dataToSave.timeWindowMinutes ?? 120)
            : null
        }
        if (isAvgParameter.value && dataToSave.timeWindowMinutes === null) {
          dataToSave.timeWindowMinutes = 15
        }

        if (editingRule.value) {
          await updateAssetNotificationRule(editingRule.value.id, dataToSave)
          toast?.success(t('components.assetNotificationRules.success.updated'))
        } else {
          await createAssetNotificationRule(dataToSave)
          toast?.success(t('components.assetNotificationRules.success.created'))
        }

        closeModal()
        rulesGrid.value?.instance?.getDataSource()?.reload()
      } catch (error) {
        console.error('Error saving rule:', error)
        toast?.error(t('components.assetNotificationRules.errors.saveFailed'))
      } finally {
        saving.value = false
      }
    }

    const toggleRuleActiveState = async (rule: any) => {
      const nextActiveState = !rule.isActive
      const title = nextActiveState
        ? t('components.assetNotificationRules.activate')
        : t('components.assetNotificationRules.deactivate')
      const message = nextActiveState
        ? t('components.assetNotificationRules.confirmActivate')
        : t('components.assetNotificationRules.confirmDeactivate')

      quasar.dialog({
        class: 'asset-notification-rules-confirm',
        title,
        message,
        cancel: true
      }).onOk(async () => {
        try {
          await updateAssetNotificationRule(rule.id, { isActive: nextActiveState })
          toast?.success(
            nextActiveState
              ? t('components.assetNotificationRules.success.activated')
              : t('components.assetNotificationRules.success.deactivated')
          )
          rulesGrid.value?.instance?.getDataSource()?.reload()
        } catch (error) {
          console.error('Error toggling rule state:', error)
          toast?.error(t('components.assetNotificationRules.errors.toggleFailed'))
        }
      })
    }

    const deleteRule = async (rule: any) => {
      quasar.dialog({
        class: 'asset-notification-rules-confirm',
        title: t('components.assetNotificationRules.deletePermanent'),
        message: t('components.assetNotificationRules.confirmDeletePermanent'),
        cancel: true
      }).onOk(async () => {
        try {
          await deleteAssetNotificationRule(rule.id)
          toast?.success(t('components.assetNotificationRules.success.deleted'))
          rulesGrid.value?.instance?.getDataSource()?.reload()
        } catch (error) {
          console.error('Error deleting rule:', error)
          toast?.error(t('components.assetNotificationRules.errors.deleteFailed'))
        }
      })
    }

    const getConditionLabel = (conditionType: string) => {
      const labels = {
        'greater_than': t('components.assetNotificationRules.conditions.greaterThan'),
        'less_than': t('components.assetNotificationRules.conditions.lessThan'),
        'equals': t('components.assetNotificationRules.conditions.equals'),
        'between': t('components.assetNotificationRules.conditions.between'),
        'outside_range': t('components.assetNotificationRules.conditions.outsideRange')
      }
      return labels[conditionType] || conditionType
    }

    const getUnitForParam = (paramName?: string) => {
      const units: Record<string, string> = {
        'TL_EYE_HUMIDITY': '%',
        'TL_EYE_HUMIDITY_AVG': '%',
        'TL_EYE_TEMPERATURE': '°C',
        'TL_EYE_TEMPERATURE_AVG': '°C',
        'TL_EYE_BATTERY_VOLTAGE': 'V',
        'DEV_POWER_SUPPLY_VOLTAGE': 'mV',
        'DEV_BATTERY_VOLTAGE': 'mV'
      }
      return units[paramName || ''] || ''
    }

    const getConditionDisplay = (rule: any) => {
      const symbols = {
        'greater_than': '>',
        'less_than': '<',
        'equals': '=',
        'between': '',
        'outside_range': ''
      }

      const symbol = symbols[rule.conditionType] || ''
      const unit = getUnitForParam(rule.canParamsIo?.paramName)

      if (BOOLEAN_STATE_PARAMS.includes(rule.canParamsIo?.paramName)) {
        const state = Number(rule.thresholdValue) === 1
          ? t('components.assetNotificationRules.form.enabled')
          : t('components.assetNotificationRules.form.disabled')

        if (rule.canParamsIo?.paramName === DWELL_PARAM && rule.timeWindowMinutes) {
          return `${state} (${rule.timeWindowMinutes} min)`
        }

        return state
      }

      if (rule.conditionType === 'between') {
        return `${rule.thresholdValue} - ${rule.thresholdValueMax}${unit ? ` ${unit}` : ''}`
      } else if (rule.conditionType === 'outside_range') {
        return `< ${rule.thresholdValue} lub > ${rule.thresholdValueMax}${unit ? ` ${unit}` : ''}`
      } else {
        return `${symbol} ${rule.thresholdValue}${unit ? ` ${unit}` : ''}`
      }
    }

    const parameterTranslations = computed(() => ({
      'TL_EYE_LOW_BATTERY': t('components.assetsList.params.battery'),
      'TL_EYE_BATTERY_VOLTAGE': t('components.assets.dataGrids.batteryVoltage'),
      'TL_EYE_HUMIDITY': t('components.assets.dataGrids.humidity'),
      'TL_EYE_TEMPERATURE': t('components.assets.dataGrids.temperature'),
      'TL_EYE_TEMPERATURE_AVG': t('components.assets.dataGrids.temperatureAvg'),
      'TL_EYE_HUMIDITY_AVG': t('components.assets.dataGrids.humidityAvg'),
      [INACTIVITY_PARAM_24H]: t('components.assetNotificationRules.parameters.inactive24h'),
      [INACTIVITY_PARAM_30M]: t('components.assetNotificationRules.parameters.inactive30m'),
      [DWELL_PARAM]: t('components.assetNotificationRules.parameters.dwell'),
      'DEV_POWER_SUPPLY_VOLTAGE': t('asset_params.DEV_POWER_SUPPLY_VOLTAGE'),
      'DEV_BATTERY_VOLTAGE': t('asset_params.DEV_BATTERY_VOLTAGE')
    }))

    const deviceSeriesParameterTranslations = computed(() => {
      const entries = Object.values(SERIES_META)
      return entries.reduce<Record<string, string>>((acc, meta) => {
        const paramName = meta?.paramName
        const labelKey = meta?.labelKey
        if (!paramName || !labelKey) {
          return acc
        }

        const isDknOrWago = paramName.startsWith('DKN_LMS_') || paramName.startsWith('WAGO_') || paramName.startsWith('DKN_UART_')
        if (isDknOrWago && te(labelKey)) {
          acc[paramName] = t(labelKey)
        }

        return acc
      }, {})
    })

    const getParameterDisplayName = (paramName: string) => {
      const predefined = parameterTranslations.value[paramName]
      if (predefined) {
        return predefined
      }

      const seriesTranslation = deviceSeriesParameterTranslations.value[paramName]
      if (seriesTranslation) {
        return seriesTranslation
      }

      const translationCandidates = [
        `components.assetDetails.dknLabels.${paramName}`,
        `asset_params.${paramName}`
      ]

      const translationKey = translationCandidates.find((key) => te(key))
      return translationKey ? t(translationKey) : paramName
    }

    const BINARY_PARAMS = ['TL_EYE_LOW_BATTERY']
    const AVG_PARAMS = ['TL_EYE_TEMPERATURE_AVG', 'TL_EYE_HUMIDITY_AVG']
    const TIME_WINDOW_OPTIONS = [
      { value: 15, label: '15 min' },
      { value: 30, label: '30 min' },
      { value: 60, label: '1h' },
      { value: 180, label: '3h' },
      { value: 360, label: '6h' }
    ]

    const selectedParameter = computed(() => (availableParameters.value as any[]).find(
      (p) => p.id === formData.value.canParamsIoId
    ))

    const isBinaryParameter = computed(
      () => !!selectedParameter.value && BINARY_PARAMS.includes(selectedParameter.value.paramName)
    )

    const isAvgParameter = computed(
      () => !!selectedParameter.value && AVG_PARAMS.includes(selectedParameter.value.paramName)
    )

    const isInactivityParameter = computed(
      () => !!selectedParameter.value && INACTIVITY_PARAMS.includes(selectedParameter.value.paramName)
    )

    const isDwellParameter = computed(
      () => selectedParameter.value?.paramName === DWELL_PARAM
    )

    const isBooleanStateParameter = computed(
      () => isInactivityParameter.value || isDwellParameter.value
    )

    const isBooleanThresholdTrue = computed(() => Number(formData.value.thresholdValue) === 1)
    const isTargetSelected = computed(() => {
      if (formData.value.targetType === 'group') return !!formData.value.targetAssetGroupId
      if (formData.value.targetType === 'gateway') return !!formData.value.targetGwId
      return !!formData.value.targetAssetId
    })
    const isParameterSelected = computed(() => !!formData.value.canParamsIoId)
    const validationErrors = computed(() => ({
      name: showValidationErrors.value && !formData.value.name?.trim(),
      targetAssetId: showValidationErrors.value && formData.value.targetType === 'asset' && !formData.value.targetAssetId,
      targetAssetGroupId: showValidationErrors.value && formData.value.targetType === 'group' && !formData.value.targetAssetGroupId,
      targetGwId: showValidationErrors.value && formData.value.targetType === 'gateway' && !formData.value.targetGwId,
      canParamsIoId: showValidationErrors.value && isTargetSelected.value && !formData.value.canParamsIoId,
      timeWindowMinutes: showValidationErrors.value
        && isDwellParameter.value
        && !hasPositiveValue(formData.value.timeWindowMinutes),
      repeatsPerSeries: showValidationErrors.value
        && (formData.value.repeatsPerSeries === null || formData.value.repeatsPerSeries < 0),
      repeatIntervalMinutes: showValidationErrors.value
        && formData.value.repeatsPerSeries > 0
        && !hasPositiveValue(formData.value.repeatIntervalMinutes),
      maxMessagesPerHour: showValidationErrors.value
        && formData.value.repeatsPerSeries > 0
        && !hasPositiveValue(formData.value.maxMessagesPerHour)
    }))

    const setBooleanThreshold = (value: boolean) => {
      formData.value.thresholdValue = value ? 1 : 0
      formData.value.conditionType = 'equals'
      formData.value.thresholdValueMax = null
    }

    // Watch for parameter change to auto-set condition type for binary params
    watch(() => formData.value.canParamsIoId, (newParamId) => {
      if (newParamId) {
        const selectedParam = selectedParameter.value
        if (selectedParam && selectedParam.paramName === DWELL_PARAM) {
          formData.value.conditionType = 'equals'
          formData.value.thresholdValue = 0
          formData.value.thresholdValueMax = null
          formData.value.timeWindowMinutes = formData.value.timeWindowMinutes ?? 120
        } else if (selectedParam && INACTIVITY_PARAMS.includes(selectedParam.paramName)) {
          formData.value.conditionType = 'equals'
          formData.value.thresholdValue = 0
          formData.value.thresholdValueMax = null
          formData.value.timeWindowMinutes = null
        } else if (selectedParam && BINARY_PARAMS.includes(selectedParam.paramName)) {
          formData.value.conditionType = 'equals'
        }
      }
    })

    onMounted(async () => {
      rulesGrid.value?.instance?.refresh()
      // Only loadGateways() depends on hasWirepas; the others don't need to wait for it.
      await Promise.all([
        loadAssets(),
        loadGroups(),
        fetchWirepasStatus().then(() => loadGateways())
      ])
    })

    return {
      availableParameters,
      availableAssets,
      availableGroups,
      availableGateways,
      hasWirepas,
      editingRule,
      formData,
      ruleModalRef,
      rulesGrid,
      saving,
      showValidationErrors,
      isReadOnly,
      rulesDataSource,
      loadAssets,
      loadGroups,
      loadGateways,
      openCreateModal,
      openEditModal,
      closeModal,
      saveRule,
      deleteRule,
      toggleRuleActiveState,
      getConditionLabel,
      getConditionDisplay,
      getUnitForParam,
      getParameterDisplayName,
      getTargetDisplayName,
      getTargetDisplayMeta,
      parameterTranslations,
      onAssetChange,
      onAssetGroupChange,
      onGatewayChange,
      onTargetTypeChange,
      isBinaryParameter,
      isAvgParameter,
      isInactivityParameter,
      isDwellParameter,
      isBooleanStateParameter,
      isBooleanThresholdTrue,
      isTargetSelected,
      isParameterSelected,
      validationErrors,
      setBooleanThreshold,
      TIME_WINDOW_OPTIONS,
    }
  }
})
</script>

<style scoped lang="scss">
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.asset-notification-rules {
  .required::after {
    content: '*';
    color: red;
    margin-left: 4px;
  }

  :deep(.dx-datagrid .dx-command-edit) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: auto !important;
    min-width: 0 !important;
    max-width: 100% !important;
  }
}

// Dark mode overrides
body.dark-theme {
  .asset-notification-rules {
    background: #1e1e2d;

    .card {
      background: #1e1e2d;
      color: #ffffff;
    }

    .card-header, .card-body {
      background: transparent;
      color: #ffffff;
    }

    h3, label, .form-label {
      color: #ffffff;
    }

    .btn, .form-control, .form-select {
      color: #ffffff;
      border-color: #4a4a5d;
    }

    .form-check-label,
    .text-muted,
    small {
      color: #ffffff;
    }

    i, .bi {
      color: #ffffff;
    }

    // DevExtreme grid icons
    :deep(.dx-datagrid .dx-icon-edit::before),
    :deep(.dx-datagrid .dx-command-edit .dx-link.dx-link-edit::before) {
      color: #009EF7 !important;
    }
    :deep(.dx-datagrid .dx-icon-trash::before),
    :deep(.dx-datagrid .dx-command-edit .dx-link.dx-link-delete::before) {
      color: #f1416c !important;
    }

    // Modal styling in dark mode
    :deep(.modal-content) {
      background-color: #1e1e2d;
      color: #ffffff;
      border: 1px solid #2c2c3a;
    }

    :deep(.modal-header),
    :deep(.modal-body),
    :deep(.modal-footer) {
      background-color: #1e1e2d;
      color: #ffffff;
      border-color: #2c2c3a;
    }

    :deep(.modal-title),
    :deep(label),
    :deep(.form-label) {
      color: #ffffff;
    }

    :deep(.form-control),
    :deep(.form-select) {
      background-color: #2c2c3a;
      color: #ffffff;
      border-color: #4a4a5d;
    }

    :deep(.form-check-label),
    :deep(.text-muted),
    :deep(small) {
      color: #ffffff;
    }

    :deep(.btn-light) {
      background-color: #2c2c3a;
      color: #ffffff;
      border-color: #4a4a5d;
    }
    :deep(.btn-light:hover) {
      color: #000000;
      background-color: #ffffff;
      border-color: #ffffff;
    }

    :deep(.btn-close) {
      filter: invert(1);
    }

    // Quasar confirm dialog (global selectors due to portal) - dark mode only
    :global(body.dark-theme .q-dialog-plugin) {
      background: #1e1e2d;
      color: #ffffff;
      border: 1px solid #2c2c3a;
    }
    :global(body.dark-theme .q-dialog-plugin .q-card__section),
    :global(body.dark-theme .q-dialog-plugin .q-dialog__title),
    :global(body.dark-theme .q-dialog-plugin .q-dialog__message) {
      background: #1e1e2d;
      color: #ffffff;
    }
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn) {
      color: #ffffff;
      background: #2c2c3a;
      border: 1px solid #4a4a5d;
    }
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:hover) {
      color: #000000;
      background: #ffffff;
    }
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:hover .q-btn__content),
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:hover .q-btn__content .block),
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:hover span) {
      color: #000000 !important;
    }
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:last-child) {
      background: #009ef7;
      border-color: #009ef7;
      color: #ffffff !important;
    }
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:last-child .q-btn__content),
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:last-child .q-btn__content .block),
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:last-child span) {
      color: #ffffff !important;
    }
    :global(body.dark-theme .q-dialog-plugin.asset-notification-rules-confirm .q-card__actions .q-btn:last-child:hover) {
      background: #0ab2ff;
      border-color: #0ab2ff;
      color: #ffffff;
    }
  }
}
</style>
